import type { IFilterSortOption } from '~/types/sort';

export type SortOrder = 'asc' | 'desc';

export interface SortOptions<T> {
  attr: keyof T | string;
  order?: SortOrder;
  backupAttr?: keyof T;
  preferredValue?: string | number | boolean;
  orderArrayAttr?: keyof T;
  orderArray?: Record<SortOrder, string[]>;
}

export function compareValues(a: any, b: any, order: SortOrder = 'asc'): number {
  if (a === b) {
    return 0;
  }

  if (a === undefined || b === undefined) {
    if (a === undefined && b !== undefined) return order === 'asc' ? -1 : 1;
    if (b === undefined && a !== undefined) return order === 'asc' ? 1 : -1;

    return 0;
  }

  const isDate = (val: any): boolean => !isNaN(Date.parse(val));
  const isString = (val: any): boolean => typeof val === 'string';

  let comparison = 0;

  if (isDate(a) && isDate(b)) {
    const dateA = a instanceof Date ? a : new Date(a);
    const dateB = b instanceof Date ? b : new Date(b);
    comparison = dateA > dateB ? 1 : -1;
    // comparison = new Date(a).getTime() - new Date(b).getTime();
  } else if (isString(a) && isString(b)) {
    comparison = a > b ? 1 : -1;
  } else {
    comparison = a > b ? 1 : -1;
  }

  return order === 'asc' ? -comparison : comparison;
}

export function getNestedValue(obj: any, path: string | number | symbol) {
  if (typeof path === 'string') {
    return path
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .reduce((acc, key) => acc && acc[key], obj);
  }

  return obj[path];
}

export function sortByCondition<T>(array: T[], options: SortOptions<T>): T[] {
  const { attr, order = 'asc', preferredValue, backupAttr, orderArrayAttr, orderArray } = options;

  return array.slice().sort((a, b) => {
    const valueA = getNestedValue(a, attr);
    const valueB = getNestedValue(b, attr);

    if (preferredValue !== undefined) {
      if (valueA === preferredValue && valueB !== preferredValue) return -1;
      if (valueB === preferredValue && valueA !== preferredValue) return 1;
    }

    if (orderArrayAttr === attr && orderArray) {
      const orderArrayValue = orderArray[order];
      return orderArrayValue.indexOf(valueA) - orderArrayValue.indexOf(valueB);
    }

    const primaryComparison = compareValues(valueA, valueB, order);

    if (primaryComparison !== 0) return primaryComparison;
    if (backupAttr) return compareValues(a[backupAttr], b[backupAttr], order);
    return 0;
  });
}

export function sortByConditions<T>(array: T[], optionsArray: SortOptions<T>[]): T[] {
  return optionsArray.reduce((sortedArray, options) => {
    return sortByCondition(sortedArray, options);
  }, array.slice());
}

export const mapOrderString = <T>(orderString: string): SortOptions<T> => {
  const [attr, order] = orderString.split(' ');

  return {
    attr: attr as keyof T,
    order: order === 'asc' ? 'desc' : 'asc',
    backupAttr: undefined,
  };
};

export const mapOrderStrings = <T>(orderStrings: string[]): SortOptions<T>[] => {
  return orderStrings.map(mapOrderString);
};

export const createSortOptions =
  (ascDirectionLabel: string, descDirectionLabel: string) =>
  (label: string, key: string): IFilterSortOption => {
    return [
      {
        label: label,
        value: `${key} asc`,
        directionLabel: ascDirectionLabel || 'Highest first',
      },
      {
        label: label,
        value: `${key} desc`,
        directionLabel: descDirectionLabel || 'Lowest first',
      },
    ];
  };

export const createTextSortOptions = createSortOptions('A-Z', 'Z-A');
export const createNumberSortOptions = createSortOptions('Highest first', 'Lowest first');
export const createDateSortOptions = createSortOptions('Oldest first', 'Newest first');

export function sortByArrayOrder<T>(array: T[], order: T[], sortOrder: SortOrder = 'asc'): T[] {
  return array.slice().sort((a, b) => {
    const indexA = order.indexOf(a);
    const indexB = order.indexOf(b);
    const comparison = indexA - indexB;
    return sortOrder === 'asc' ? comparison : -comparison;
  });
}
